import type { Editor } from '@tiptap/react';

let tiptapEditor: Editor | null = null;
let savedTiptapSelection: { from: number; to: number } | null = null;
let cachedTextareaSelection: { start: number; end: number } | null = null;

export function registerVariableInsertEditor(editor: Editor | null) {
  tiptapEditor = editor;
}

if (typeof document !== 'undefined') {
  document.addEventListener('selectionchange', () => {
    const el = document.activeElement;
    if (el instanceof HTMLTextAreaElement && el.name === 'content') {
      cachedTextareaSelection = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      };
    }
  });
}

export const appendHtml = (html = '', value: string | number) => {
  const pattern = /<\/p>(?![\s\S]*<\/p>)/;
  const newHtml = html.replace(pattern, `{{${value}}}</p>`);
  return newHtml;
};

export function appendContentVariable(
  content = '',
  value: string | number
): string {
  const token = `{{${value}}}`;
  if (/<\/p>/i.test(content)) {
    return appendHtml(content, value);
  }
  if (!content) return token;
  const needsSpace = !/\s$/.test(content);
  return needsSpace ? `${content} ${token}` : `${content}${token}`;
}

function getContentTextarea() {
  return document.querySelector<HTMLTextAreaElement>(
    'textarea[name="content"]'
  );
}

function isTiptapActive() {
  const active = document.activeElement;
  if (!active) return false;
  if (active.classList?.contains('ProseMirror')) return true;
  return Boolean(active.closest?.('.ProseMirror'));
}

type SavedInsertTarget =
  | { kind: 'textarea'; el: HTMLTextAreaElement; start: number; end: number }
  | { kind: 'tiptap'; from: number; to: number };

let savedInsertTarget: SavedInsertTarget | null = null;

/** Call before focus leaves the editor (e.g. dropdown trigger pointerdown capture). */
export function captureContentFieldSelection() {
  const textarea = getContentTextarea();

  if (textarea && (document.activeElement === textarea || !isTiptapActive())) {
    const start =
      textarea.selectionStart ??
      cachedTextareaSelection?.start ??
      textarea.value.length;
    const end = textarea.selectionEnd ?? cachedTextareaSelection?.end ?? start;

    savedTiptapSelection = null;
    savedInsertTarget = { kind: 'textarea', el: textarea, start, end };
    return;
  }

  if (tiptapEditor && !tiptapEditor.isDestroyed) {
    const { from, to } = tiptapEditor.state.selection;
    savedTiptapSelection = { from, to };
    savedInsertTarget = { kind: 'tiptap', from, to };
    return;
  }

  savedTiptapSelection = null;
  savedInsertTarget = null;
}

/** `string` = textarea updated text, `false` = tiptap inserted, `null` = append fallback */
export function insertContentVariableAtCursor(
  content: string,
  value: string | number
): string | false | null {
  const target = savedInsertTarget;
  savedInsertTarget = null;
  if (!target) return null;

  const token = `{{${value}}}`;

  if (target.kind === 'textarea') {
    const { start, end, el } = target;
    const next = content.slice(0, start) + token + content.slice(end);
    const cursor = start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
    return next;
  }

  if (tiptapEditor && !tiptapEditor.isDestroyed) {
    const { from, to } = savedTiptapSelection ?? target;
    savedTiptapSelection = null;

    tiptapEditor
      .chain()
      .focus()
      .setTextSelection({ from, to })
      .insertContent(token)
      .run();

    return false;
  }

  savedTiptapSelection = null;
  return null;
}

export function getTiptapEditorHtml() {
  if (!tiptapEditor || tiptapEditor.isDestroyed) return null;
  return tiptapEditor.isEmpty ? '' : tiptapEditor.getHTML();
}
